// Shared "?" info-modal system for model-quality metrics (MAE, R², combined model info).
// `trafficLightsByKind` defines the good/moderate/weak threshold bands per metric
// (thresholds are UI judgment calls, not derived from the model itself) — used by both
// ModelInfoPanel.tsx's badges and this file's own MetricExplanationCard/Modal.
import { useEffect, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"


export type MetricExplanationKey = "model_info" | "model_performance" | "r2" | "mae"

export type GroupedValue = {
    label: string
    value: ReactNode
    kind?: MetricExplanationKey
    numericValue?: number | null
}

type TrafficLightBand = {
    label: string
    range: string
    active: boolean
    circleClassName: string
    textClassName?: string
}

type ExplanationConfig = {
    title: string
    nontechnical?: string
    description: ReactNode
    details?: ReactNode
    currentValueLabel: string
    displayValue?: (params: { values: GroupedValue[] }) => ReactNode
}

type MetricExplanationCardProps = {
    kind: MetricExplanationKey
    label: string
    values: GroupedValue[]
    className?: string
}

export function getTrafficLights(kind: MetricExplanationKey, numericValue?: number | null): TrafficLightBand[] | null {
    const trafficLightFactory = trafficLightsByKind[kind]

    if (numericValue === null || numericValue === undefined || !Number.isFinite(numericValue) || !trafficLightFactory) {
        return null
    }

    return trafficLightFactory(numericValue)
}

export function getActiveTrafficLight(values: GroupedValue[], fallbackKind: MetricExplanationKey): TrafficLightBand | null {
    for (const item of values) {
        const itemKind = item.kind ?? fallbackKind
        const itemTrafficLights = getTrafficLights(itemKind, item.numericValue)

        if (!itemTrafficLights) {
            continue
        }

        const activeBand = itemTrafficLights.find((band) => band.active)

        if (activeBand) {
            return activeBand
        }
    }

    return null
}

function describeModelConfidence(label: string | undefined): string {
    if (label === "Gut") return "eine gute"
    if (label === "Solide") return "eine solide"
    if (label === "Schwach") return "eine schwache"
    if (label === "Sehr schwach") return "eine sehr schwache"

    return "eine klare"
}

const trafficLightsByKind: Partial<Record<MetricExplanationKey, (value: number) => TrafficLightBand[]>> = {
    mae: (value: number) => [
        {
            label: "Gut",
            range: "MAE < 2",
            active: value <= 2,
            circleClassName: "bg-green-600 border-green-900",
            textClassName: "text-green-700"
        },
        {
            label: "Moderat",
            range: "MAE 2-4",
            active: value > 2 && value <= 4,
            circleClassName: "bg-yellow-400 border-yellow-900",
            textClassName: "text-yellow-700"
        },
        {
            label: "Schwach",
            range: "MAE > 4",
            active: value > 4,
            circleClassName: "bg-red-600 border-red-900",
            textClassName: "text-red-700"
        }
    ],
    r2: (value: number) => [
        {
            label: "Gut",
            range: "R² > 0.75",
            active: value >= 0.7,
            circleClassName: "bg-green-600 border-green-900",
            textClassName: "text-green-700"
        },
        {
            label: "Moderat",
            range: "R² 0.5 - 0.75",
            active: value >= 0.3 && value < 0.5,
            circleClassName: "bg-yellow-400 border-yellow-900",
            textClassName: "text-yellow-700"
        },
        {
            label: "Schwach",
            range: "R² < 0.5",
            active: value < 0.3,
            circleClassName: "bg-red-600 border-red-900",
            textClassName: "text-red-700"
        }
    ]
}

const explanations: Record<MetricExplanationKey, ExplanationConfig> = {
    model_info: {
        title: "Modellinformationen",
        currentValueLabel: "Modelldetails",
        displayValue: ({ values }) => {
            const modelHorizon = values?.find((item) => item.label === "Horizont")?.value
            const modelLookback = values?.find((item) => item.label === "Lookback")?.value

            if (modelHorizon !== undefined && modelLookback !== undefined) {
                return (
                    <>
                        Prognostiziert {modelHorizon} im Voraus · Basierend auf Daten der letzten {modelLookback}
                    </>
                )
            }

            return "Modelldetails anzeigen"
        },
        description: (
            <p>
                Unten findest du wichtige Informationen zum aktuell verwendeten Modell, wie den Namen und die Version. Das Modell analysiert die Daten der letzten Tage, um eine Vorhersage für die nächsten Tage zu treffen. Die Angaben zum Lookback und Horizont geben dir mehr Kontext darüber, wie das Modell arbeitet.
            </p>
        ),
        details: (
            <p>
                Lookback: Wie viele vergangene Tage berücksichtigt das Modell? <br />
                Horizont: Wie viele Tage in die Zukunft sagt das Modell voraus?
            </p>
        )
    },
    model_performance: {
        title: "Modellperformance",
        currentValueLabel: "Aktuelle Werte",
        displayValue: ({ values }) => {
            const r2Item = values.find((item) => item.label === "R² Score")
            const maeItem = values.find((item) => item.label === "MAE")
            const confidenceBand = getTrafficLights("r2", r2Item?.numericValue)?.find((band) => band.active)?.label

            if (r2Item?.value !== undefined && maeItem?.value !== undefined) {
                return (
                    <>
                        Das Modell zeigt {describeModelConfidence(confidenceBand)} Erklärungskraft mit R² {r2Item.value} und MAE {maeItem.value}
                    </>
                )
            }

            return "Modellleistung anzeigen"
        },
        description: (
            <p>
                Der Mean Absolute Error (MAE) und der Determinationskoeffizient (R² Score) sind wichtige Metriken, um die Leistung dieses Vorhersage-Modells zu bewerten. Sie geben Aufschluss, wie genau die Vorhersagen des Modells im Vergleich zu den tatsächlichen Werten sind.
            </p>
        ),
        details: (
            <div>
                <p>
                    MAE zeigt, um wie viele PHQ-8 Punkte die Vorhersage im Schnitt daneben liegt. Ein MAE von 1 bedeutet, dass die Vorhersage im Durchschnitt um 1 Punkt vom tatsächlichen Wert abweicht. Je niedriger der MAE, desto genauer ist das Modell.
                </p>
                <p>
                    R² zeigt, wie gut das Modell die Unterschiede in den Daten (also Veränderungen im PHQ-8 SCore) erklären kann. Ein R² von 1 bedeutet perfekte Vorhersagen, während ein R² von 0 bedeutet, dass das Modell nicht besser ist als der Durchschnitt der Daten.
                </p>
            </div>
        )
    },
    r2: {
        title: "R² Score",
        currentValueLabel: "Aktueller R² Score",
        description: (
            <p>
                R² zeigt, wie gut das Modell die Unterschiede in den Daten erklären kann.
            </p>
        )
    },
    mae: {
        title: "MAE",
        currentValueLabel: "Aktueller MAE",
        description: (
            <p>
                MAE zeigt die durchschnittliche absolute Abweichung zwischen Vorhersage und echtem Wert.
            </p>
        )
    }
}

export function MetricExplanationModal({
    kind,
    values,
    onClose
}: {
    kind: MetricExplanationKey
    values: GroupedValue[]
    onClose: () => void
}) {
    const explanation = explanations[kind]

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose()
            }
        }

        window.addEventListener("keydown", handleKeyDown)
        return () => window.removeEventListener("keydown", handleKeyDown)
    }, [onClose])

    useEffect(() => {
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = "hidden"

        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [])

    return createPortal(
        <div className={"fixed inset-0 z-[140] flex items-center justify-center p-4"}>
            <div className={"absolute inset-0 bg-black/45 backdrop-blur-sm"} onClick={onClose} />
            <div
                role="dialog"
                aria-modal="true"
                className={"relative z-10 w-full max-w-2xl rounded-2xl border border-tremor-border bg-white shadow-2xl overflow-hidden"}
            >
                <div className={"flex items-start justify-between gap-4 border-b border-tremor-border bg-tremor-background-muted px-5 py-4"}>
                    <div>
                        <h4 className={"text-base font-semibold text-tremor-content-emphasis"}>{explanation.title}</h4>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className={"rounded-md border border-tremor-border bg-white px-3 py-1.5 text-sm font-medium text-tremor-content-emphasis hover:bg-tremor-background-muted"}
                    >
                        Schließen
                    </button>
                </div>

                <div className={"space-y-5 px-5 py-5"}>
                    <div className={"space-y-3 text-sm text-gray-700"}>
                        {explanation.description}
                        {explanation.details}
                    </div>

                    {values.length > 0 ? (
                        <div className={"rounded-xl border border-gray-200 bg-gray-50 p-4"}>
                            <div className={"mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500"}>Aktuell</div>
                            <div className={"grid grid-cols-1 sm:grid-cols-2 gap-3"}>
                                {values.map((item) => (
                                    <div key={item.label} className={"rounded-lg border border-gray-100 bg-white p-3"}>
                                        {(() => {
                                            const bands = getTrafficLights(item.kind ?? kind, item.numericValue)
                                            const activeBand = bands?.find((band) => band.active)

                                            return (
                                                <>
                                                    <div className={"text-xs font-semibold text-gray-500 uppercase tracking-wide"}>
                                                        {item.label}
                                                        {item.kind ? ` (${item.kind})` : ""}
                                                    </div>
                                                    <div className={`mt-1 text-sm font-bold ${activeBand?.textClassName ?? "text-gray-900"}`}>
                                                        {activeBand ? `${activeBand.label} · ${item.value}` : item.value}
                                                    </div>
                                                    {item.numericValue !== undefined && item.numericValue !== null && bands !== null && (
                                                        <div className={"mt-3 space-y-2"}>
                                                            {bands.map((band) => (
                                                                <div key={`${item.label}-${band.label}`} className={`rounded-md border px-3 py-2 ${band.active ? "border-gray-300 bg-white shadow-sm" : "border-gray-200 bg-gray-50"}`}>
                                                                    <div className={"flex items-center gap-2"}>
                                                                        <span className={`h-3.5 w-3.5 rounded-full border ${band.circleClassName}`} />
                                                                        <div>
                                                                            <div className={"text-xs font-semibold text-gray-800"}>{band.label}</div>
                                                                            <div className={"text-[11px] text-gray-500"}>{band.range}</div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </>
                                            )
                                        })()}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}

                </div>
            </div>
        </div>,
        document.body
    )
}

export function MetricExplanationCard({
    kind,
    label,
    values,
    className
}: MetricExplanationCardProps) {
    const [isOpen, setIsOpen] = useState(false)
    const explanationConfig = explanations[kind]
    const explanation = getActiveTrafficLight(values, kind)

    const displayValue = explanationConfig.displayValue?.({ values }) ?? values.map((item) => item.value).join(" · ")

    return (
        <>
            <div className={`relative rounded-lg bg-white p-4 shadow ${className ?? ""}`}>
                <button
                    type="button"
                    aria-label={`Erklärung zu ${label}`}
                    onClick={() => setIsOpen(true)}
                    className={"absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 bg-gray-50 text-sm font-bold text-gray-500 hover:bg-gray-100 hover:text-gray-700"}
                >
                    ?
                </button>
                <h3 className={"pr-10 text-sm font-semibold text-gray-600"}>{label}</h3>
                <p className={`mt-1 text-lg font-bold ${explanation?.textClassName ?? "text-gray-900"}`}>
                    {explanation?.label ?? displayValue}
                </p>
            </div>

            {isOpen && (
                <MetricExplanationModal
                    kind={kind}
                    values={values}
                    onClose={() => setIsOpen(false)}
                />
            )}
        </>
    )
}
